import { useEffect, useState } from 'react';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { useMutation } from '@tanstack/react-query';
import { createSubscriptionPayment } from '@/api/query/payment';
import { useGuestAuth } from '@/common/contexts/GuestAuthContext';
import {
	getMailLinkKey,
	getPaymentBrowsingPersonId,
	getPaymentUtms,
	isQuizPaymentContext,
} from '@/common/functions/getUtms';
import { normalizePlanName } from '@/common/constants/planNames';
import { PLAN_PAYMENT_ERROR_KEY } from '../../../constants/planPayment.constants';

export const useStripePaymentForm = ({
	selectedPlan,
	returnPath = '/studio',
	billingCountry,
}) => {
	const stripe = useStripe();
	const elements = useElements();
	const { user } = useGuestAuth();
	const [confirmError, setConfirmError] = useState('');
	const [isProcessing, setIsProcessing] = useState(false);

	useEffect(() => {
		const storedError = sessionStorage.getItem(PLAN_PAYMENT_ERROR_KEY);
		if (!storedError) {
			return;
		}

		sessionStorage.removeItem(PLAN_PAYMENT_ERROR_KEY);
		setConfirmError(storedError);
	}, []);

	const handlePaymentMutation = useMutation({
		mutationFn: async () => {
			const mailLinkKey = getMailLinkKey();
			const browsingPerson = getPaymentBrowsingPersonId();
			const payload = {
				plan: normalizePlanName(selectedPlan.name),
				type: 'STRIPE',
				paymentFor: 'PLAN',
				paymentUtms: getPaymentUtms(),
				customerId: user?.id,
				customerEmail: user?.email,
				fromQuiz: isQuizPaymentContext(),
			};

			if (browsingPerson) {
				payload.browsingPerson = browsingPerson;
			}

			if (mailLinkKey) {
				payload.mailLinkKey = mailLinkKey;
			}

			const { data } = await createSubscriptionPayment(payload);

			if (!data.clientSecret) {
				throw new Error('Failed to initialize subscription payment.');
			}

			const confirmIntent =
				data.type === 'setup' ? stripe.confirmSetup : stripe.confirmPayment;

			const encodedReturnPath = encodeURIComponent(returnPath || '/studio');
			const encodedEmail = encodeURIComponent(user?.email || '');

			const { error } = await confirmIntent({
				elements,
				clientSecret: data.clientSecret,
				confirmParams: {
					return_url: `${window.location.origin}/order-status?result=success&paymentFor=plan&returnPath=${encodedReturnPath}&email=${encodedEmail}`,
					payment_method_data: {
						billing_details: {
							address: {
								country: billingCountry,
							},
						},
					},
				},
			});

			if (error) {
				throw new Error(error.message);
			}
		},
	});

	const handleSubmit = async (event) => {
		event.preventDefault();
		setConfirmError('');

		if (!stripe || !elements || isProcessing) {
			return;
		}

		setIsProcessing(true);

		try {
			const { error: submitError } = await elements.submit();
			if (submitError) {
				setConfirmError(submitError.message ?? 'Payment validation failed.');
				return;
			}

			await handlePaymentMutation.mutateAsync();
		} catch (error) {
			setConfirmError(error?.message ?? 'Payment failed.');
		} finally {
			setIsProcessing(false);
		}
	};

	return {
		stripe,
		elements,
		isProcessing,
		errorMessage: confirmError || handlePaymentMutation.error?.message || '',
		handleSubmit,
	};
};
